import React, { useMemo, useRef, useEffect, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { Card, Spin, Empty, Space, Select } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { InspectionLocationResponse } from '@/services/reports.service';
import { InspectionItemStatus, InspectionItemStatusLabels } from '@/shared/enums/InspectionStatus';

// Fix default marker icons for Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface InspectionMapViewProps {
  data: InspectionLocationResponse[];
  loading?: boolean;
  onInspectionClick?: (inspectionId: string, itemId: string) => void;
}

// Função para criar ícone circular com contador
const createCircleIcon = (count: number): L.DivIcon => {
  const size = count > 99 ? 50 : count > 9 ? 40 : 35;
  const fontSize = count > 99 ? 12 : count > 9 ? 14 : 16;

  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background-color: #722ed1;
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${fontSize}px;
        box-shadow: 0 3px 10px rgba(0,0,0,0.4);
        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      ">
        ${count}
      </div>
    `,
    className: 'custom-marker-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Função para obter cor do status
const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    'LaudoAceitoPeloBanco': '#52c41a',
    'AguardandoAceiteInspetor': '#52c41a',
    'AguardandoDirecionamentoInspetor': '#722ed1',
    'AguardandoRevisaoPrestadora': '#1890ff',
    'AguardandoDirecionamento': '#fa8c16',
    'Agendado': '#13c2c2',
    'Cancelado': '#ff4d4f',
    'Frustrado': '#ff7a45',
    'CheckinRealizado': '#eb2f96',
    'Finalizado': '#1890ff',
    'AguardandoAgendamento': '#faad14',
    'AguardandoRevisaoBanco': '#722ed1',
    'AguardandoGeracaoLaudo': '#eb2f96',
    'AguardandoAceitePrestadora': '#1890ff',
    'PrestadoraRecusou': '#ff4d4f',
    'InspetorRecusou': '#ff4d4f',
    'LaudoNegadoPorPrestadora': '#fa8c16',
    'LaudoNegadoPorBanco': '#ff4d4f',
    'CorrecaoPeloInspetorBanco': '#fa8c16',
    'TelevistoriaAgendada': '#722ed1',
    'TelevistoriaFinalizada': '#13c2c2',
    'LaudoDevolvido': '#ff7a45',
  };

  return statusColors[status] || '#d9d9d9';
};

// Componente de cluster de marcadores
interface MarkerClusterGroupProps {
  inspections: InspectionLocationResponse[];
  onInspectionClick?: (inspectionId: string, itemId: string) => void;
}

function MarkerClusterGroup({ inspections, onInspectionClick }: MarkerClusterGroupProps) {
  const map = useMap();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {
    if (!map) return;

    // Remover cluster anterior se existir
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    // Criar novo cluster group
    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 50,
      iconCreateFunction: (cluster) => {
        const childMarkers = cluster.getAllChildMarkers();
        let totalCount = 0;

        childMarkers.forEach((marker: any) => {
          const groupInspections = marker.options.inspectionData as InspectionLocationResponse[];
          if (groupInspections) {
            totalCount += groupInspections.length;
          }
        });

        const size = totalCount > 99 ? 50 : totalCount > 9 ? 40 : 35;
        const fontSize = totalCount > 99 ? 12 : totalCount > 9 ? 14 : 16;

        return L.divIcon({
          html: `
            <div style="
              width: ${size}px;
              height: ${size}px;
              background-color: #722ed1;
              border: 3px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: ${fontSize}px;
              box-shadow: 0 3px 10px rgba(0,0,0,0.4);
              text-shadow: 0 1px 2px rgba(0,0,0,0.5);
            ">
              ${totalCount}
            </div>
          `,
          className: 'marker-cluster-custom',
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      },
    });

    // Agrupar inspeções por localização exata
    const groups: Map<string, InspectionLocationResponse[]> = new Map();
    inspections.forEach((inspection) => {
      if (!inspection.latitude || !inspection.longitude) return;
      const key = `${inspection.latitude.toFixed(6)}_${inspection.longitude.toFixed(6)}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(inspection);
    });

    // Criar marcadores para cada grupo
    groups.forEach((groupInspections) => {
      const firstInspection = groupInspections[0];
      if (!firstInspection || !firstInspection.latitude || !firstInspection.longitude) return;

      const icon = createCircleIcon(groupInspections.length);
      const locationKey = `${firstInspection.latitude.toFixed(6)}_${firstInspection.longitude.toFixed(6)}`;

      const marker = L.marker([firstInspection.latitude, firstInspection.longitude], {
        icon,
        inspectionData: groupInspections,
        locationKey,
      } as any);

      // Helper para formatar datas
      const formatDate = (dateString?: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      };

      const formatDateTime = (dateString?: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      };

      // Helper para obter label do status
      const getStatusLabel = (status: string): string => {
        // Verificar se o status é uma chave válida do enum
        if (status in InspectionItemStatus) {
          const numericValue = InspectionItemStatus[status as keyof typeof InspectionItemStatus];
          if (typeof numericValue === 'number' && numericValue in InspectionItemStatusLabels) {
            return InspectionItemStatusLabels[numericValue];
          }
        }
        return status;
      };

      // Criar conteúdo do popup
      const popupContent = document.createElement('div');
      popupContent.style.maxHeight = '500px';
      popupContent.style.overflowY = 'auto';
      popupContent.style.padding = '12px';

      if (groupInspections.length > 1) {
        // Múltiplas inspeções na mesma localização
        const title = document.createElement('h3');
        title.style.margin = '0 0 16px 0';
        title.style.fontSize = '16px';
        title.style.fontWeight = '600';
        title.textContent = `${groupInspections.length} inspeções neste local`;
        popupContent.appendChild(title);

        groupInspections.forEach((inspection, idx) => {
          const card = document.createElement('div');
          card.style.padding = '14px';
          card.style.marginBottom = idx < groupInspections.length - 1 ? '14px' : '0';
          card.style.border = '1px solid #f0f0f0';
          card.style.borderRadius = '8px';
          card.style.backgroundColor = '#fff';

          // Código da inspeção
          const codeEl = document.createElement('div');
          codeEl.style.fontWeight = '600';
          codeEl.style.fontSize = '14px';
          codeEl.style.marginBottom = '10px';
          codeEl.textContent = inspection.inspectionCode;
          card.appendChild(codeEl);

          // Status
          const statusEl = document.createElement('div');
          statusEl.style.marginBottom = '10px';
          const statusBadge = document.createElement('span');
          statusBadge.style.backgroundColor = getStatusColor(inspection.status);
          statusBadge.style.color = '#fff';
          statusBadge.style.padding = '3px 10px';
          statusBadge.style.borderRadius = '4px';
          statusBadge.style.fontSize = '12px';
          statusBadge.style.fontWeight = '600';
          statusBadge.textContent = getStatusLabel(inspection.status);
          statusEl.appendChild(statusBadge);
          card.appendChild(statusEl);

          // Tipo de inspeção
          if (inspection.inspectionType) {
            const typeEl = document.createElement('div');
            typeEl.style.fontSize = '12px';
            typeEl.style.color = '#666';
            typeEl.style.marginBottom = '8px';
            typeEl.innerHTML = `<span style="font-weight: 500;">Tipo:</span> ${inspection.inspectionType}`;
            card.appendChild(typeEl);
          }

          // Inspetor
          if (inspection.inspectorName) {
            const inspectorEl = document.createElement('div');
            inspectorEl.style.fontSize = '12px';
            inspectorEl.style.color = '#666';
            inspectorEl.style.marginBottom = '8px';
            inspectorEl.innerHTML = `<span style="font-weight: 500;">Inspetor:</span> ${inspection.inspectorName}`;
            card.appendChild(inspectorEl);
          }

          // Prestadora
          if (inspection.providerName) {
            const providerEl = document.createElement('div');
            providerEl.style.fontSize = '12px';
            providerEl.style.color = '#666';
            providerEl.style.marginBottom = '10px';
            providerEl.innerHTML = `<span style="font-weight: 500;">Prestadora:</span> ${inspection.providerName}`;
            card.appendChild(providerEl);
          }

          // Valor avaliado
          if (inspection.evaluatedValue && inspection.evaluatedValue > 0) {
            const valueEl = document.createElement('div');
            valueEl.style.fontSize = '14px';
            valueEl.style.color = '#52c41a';
            valueEl.style.fontWeight = '700';
            valueEl.style.marginBottom = '10px';
            valueEl.textContent = new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            }).format(inspection.evaluatedValue);
            card.appendChild(valueEl);
          }

          // Data de criação
          if (inspection.createdAt) {
            const createdEl = document.createElement('div');
            createdEl.style.fontSize = '11px';
            createdEl.style.color = '#999';
            createdEl.style.marginBottom = '4px';
            createdEl.innerHTML = `Criado: ${formatDateTime(inspection.createdAt)}`;
            card.appendChild(createdEl);
          }

          // Data agendada
          if (inspection.scheduledDate) {
            const scheduledEl = document.createElement('div');
            scheduledEl.style.fontSize = '11px';
            scheduledEl.style.color = '#999';
            scheduledEl.style.marginBottom = '4px';
            scheduledEl.innerHTML = `Agendado: ${formatDate(inspection.scheduledDate)}`;
            card.appendChild(scheduledEl);
          }

          // Data de conclusão
          if (inspection.completedDate) {
            const completedEl = document.createElement('div');
            completedEl.style.fontSize = '11px';
            completedEl.style.color = '#999';
            completedEl.style.marginBottom = '10px';
            completedEl.innerHTML = `Concluído: ${formatDate(inspection.completedDate)}`;
            card.appendChild(completedEl);
          }

          // Botão ver detalhes
          const btnContainer = document.createElement('div');
          btnContainer.style.marginTop = '10px';
          const detailsBtn = document.createElement('button');
          detailsBtn.style.width = '100%';
          detailsBtn.style.padding = '6px 14px';
          detailsBtn.style.border = 'none';
          detailsBtn.style.borderRadius = '4px';
          detailsBtn.style.backgroundColor = '#1890ff';
          detailsBtn.style.color = '#fff';
          detailsBtn.style.cursor = 'pointer';
          detailsBtn.style.fontSize = '13px';
          detailsBtn.style.fontWeight = '500';
          detailsBtn.textContent = 'Ver Detalhes';
          detailsBtn.onclick = () => {
            if (onInspectionClick) {
              onInspectionClick(inspection.inspectionId, inspection.itemId);
            }
          };
          btnContainer.appendChild(detailsBtn);
          card.appendChild(btnContainer);

          popupContent.appendChild(card);
        });
      } else {
        // Inspeção única
        const inspection = groupInspections[0];

        const codeEl = document.createElement('div');
        codeEl.style.fontWeight = '700';
        codeEl.style.fontSize = '16px';
        codeEl.style.marginBottom = '12px';
        codeEl.style.color = '#262626';
        codeEl.textContent = inspection.inspectionCode;
        popupContent.appendChild(codeEl);

        // Status
        const statusEl = document.createElement('div');
        statusEl.style.marginBottom = '12px';
        const statusBadge = document.createElement('span');
        statusBadge.style.backgroundColor = getStatusColor(inspection.status);
        statusBadge.style.color = '#fff';
        statusBadge.style.padding = '4px 12px';
        statusBadge.style.borderRadius = '4px';
        statusBadge.style.fontSize = '12px';
        statusBadge.style.fontWeight = '600';
        statusBadge.textContent = getStatusLabel(inspection.status);
        statusEl.appendChild(statusBadge);
        popupContent.appendChild(statusEl);

        // Endereço
        const addressEl = document.createElement('div');
        addressEl.style.fontSize = '13px';
        addressEl.style.color = '#595959';
        addressEl.style.marginBottom = '6px';
        addressEl.style.lineHeight = '1.4';
        addressEl.innerHTML = `${inspection.address}`;
        popupContent.appendChild(addressEl);

        // Cidade/Estado
        const locationEl = document.createElement('div');
        locationEl.style.fontSize = '12px';
        locationEl.style.color = '#8c8c8c';
        locationEl.style.marginBottom = '14px';
        locationEl.textContent = `${inspection.city}/${inspection.state}`;
        popupContent.appendChild(locationEl);

        // Divider
        const divider = document.createElement('div');
        divider.style.borderTop = '1px solid #f0f0f0';
        divider.style.margin = '14px 0';
        popupContent.appendChild(divider);

        // Tipo
        if (inspection.inspectionType) {
          const typeEl = document.createElement('div');
          typeEl.style.fontSize = '13px';
          typeEl.style.marginBottom = '10px';
          typeEl.innerHTML = `<span style="color: #8c8c8c;">Tipo:</span> <span style="font-weight: 600; color: #262626;">${inspection.inspectionType}</span>`;
          popupContent.appendChild(typeEl);
        }

        // Inspetor
        if (inspection.inspectorName) {
          const inspectorEl = document.createElement('div');
          inspectorEl.style.fontSize = '13px';
          inspectorEl.style.marginBottom = '10px';
          inspectorEl.innerHTML = `<span style="color: #8c8c8c;">Inspetor:</span> <span style="font-weight: 600; color: #262626;">${inspection.inspectorName}</span>`;
          popupContent.appendChild(inspectorEl);
        }

        // Prestadora
        if (inspection.providerName) {
          const providerEl = document.createElement('div');
          providerEl.style.fontSize = '13px';
          providerEl.style.marginBottom = '10px';
          providerEl.innerHTML = `<span style="color: #8c8c8c;">Prestadora:</span> <span style="font-weight: 600; color: #262626;">${inspection.providerName}</span>`;
          popupContent.appendChild(providerEl);
        }

        // Valor avaliado
        if (inspection.evaluatedValue && inspection.evaluatedValue > 0) {
          const valueEl = document.createElement('div');
          valueEl.style.fontSize = '18px';
          valueEl.style.fontWeight = '700';
          valueEl.style.color = '#52c41a';
          valueEl.style.marginTop = '14px';
          valueEl.style.marginBottom = '14px';
          valueEl.textContent = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(inspection.evaluatedValue);
          popupContent.appendChild(valueEl);
        }

        // Divider antes das datas
        const divider2 = document.createElement('div');
        divider2.style.borderTop = '1px solid #f0f0f0';
        divider2.style.margin = '14px 0';
        popupContent.appendChild(divider2);

        // Datas
        const datesContainer = document.createElement('div');
        datesContainer.style.backgroundColor = '#fafafa';
        datesContainer.style.padding = '10px';
        datesContainer.style.borderRadius = '6px';
        datesContainer.style.marginBottom = '14px';

        // Data de criação
        if (inspection.createdAt) {
          const createdEl = document.createElement('div');
          createdEl.style.fontSize = '12px';
          createdEl.style.color = '#595959';
          createdEl.style.marginBottom = '6px';
          createdEl.innerHTML = `<span style="font-weight: 500;">Criado:</span> ${formatDateTime(inspection.createdAt)}`;
          datesContainer.appendChild(createdEl);
        }

        // Data agendada
        if (inspection.scheduledDate) {
          const scheduledEl = document.createElement('div');
          scheduledEl.style.fontSize = '12px';
          scheduledEl.style.color = '#595959';
          scheduledEl.style.marginBottom = '6px';
          scheduledEl.innerHTML = `<span style="font-weight: 500;">Agendado:</span> ${formatDate(inspection.scheduledDate)}`;
          datesContainer.appendChild(scheduledEl);
        }

        // Data de conclusão
        if (inspection.completedDate) {
          const completedEl = document.createElement('div');
          completedEl.style.fontSize = '12px';
          completedEl.style.color = '#595959';
          completedEl.innerHTML = `<span style="font-weight: 500;">Concluído:</span> ${formatDate(inspection.completedDate)}`;
          datesContainer.appendChild(completedEl);
        }

        if (datesContainer.children.length > 0) {
          popupContent.appendChild(datesContainer);
        }

        // Botão ver detalhes
        const btnContainer = document.createElement('div');
        btnContainer.style.marginTop = '14px';
        const detailsBtn = document.createElement('button');
        detailsBtn.style.width = '100%';
        detailsBtn.style.padding = '8px 16px';
        detailsBtn.style.border = 'none';
        detailsBtn.style.borderRadius = '4px';
        detailsBtn.style.backgroundColor = '#1890ff';
        detailsBtn.style.color = '#fff';
        detailsBtn.style.cursor = 'pointer';
        detailsBtn.style.fontSize = '14px';
        detailsBtn.style.fontWeight = '500';
        detailsBtn.textContent = 'Ver Detalhes';
        detailsBtn.onmouseover = () => {
          detailsBtn.style.backgroundColor = '#40a9ff';
        };
        detailsBtn.onmouseout = () => {
          detailsBtn.style.backgroundColor = '#1890ff';
        };
        detailsBtn.onclick = () => {
          if (onInspectionClick) {
            onInspectionClick(inspection.inspectionId, inspection.itemId);
          }
        };
        btnContainer.appendChild(detailsBtn);
        popupContent.appendChild(btnContainer);
      }

      marker.bindPopup(popupContent, {
        maxWidth: groupInspections.length > 1 ? 1350 : 1200,
        maxHeight: 650
      });
      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [map, inspections, onInspectionClick]);

  return null;
}

const InspectionMapView: React.FC<InspectionMapViewProps> = ({
  data,
  loading = false,
  onInspectionClick,
}) => {
  // Estado do filtro de status
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);

  // Status options - todos os status do enum InspectionItemStatus
  const statusOptions = [
    { value: InspectionItemStatus.AguardandoDirecionamento, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoDirecionamento] },
    { value: InspectionItemStatus.AguardandoAceitePrestadora, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoAceitePrestadora] },
    { value: InspectionItemStatus.PrestadoraRecusou, label: InspectionItemStatusLabels[InspectionItemStatus.PrestadoraRecusou] },
    { value: InspectionItemStatus.AguardandoDirecionamentoInspetor, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoDirecionamentoInspetor] },
    { value: InspectionItemStatus.AguardandoAceiteInspetor, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoAceiteInspetor] },
    { value: InspectionItemStatus.InspetorRecusou, label: InspectionItemStatusLabels[InspectionItemStatus.InspetorRecusou] },
    { value: InspectionItemStatus.AguardandoAgendamento, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoAgendamento] },
    { value: InspectionItemStatus.Agendado, label: InspectionItemStatusLabels[InspectionItemStatus.Agendado] },
    { value: InspectionItemStatus.CheckinRealizado, label: InspectionItemStatusLabels[InspectionItemStatus.CheckinRealizado] },
    { value: InspectionItemStatus.Finalizado, label: InspectionItemStatusLabels[InspectionItemStatus.Finalizado] },
    { value: InspectionItemStatus.Cancelado, label: InspectionItemStatusLabels[InspectionItemStatus.Cancelado] },
    { value: InspectionItemStatus.Frustrado, label: InspectionItemStatusLabels[InspectionItemStatus.Frustrado] },
    { value: InspectionItemStatus.AguardandoGeracaoLaudo, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoGeracaoLaudo] },
    { value: InspectionItemStatus.AguardandoRevisaoPrestadora, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoRevisaoPrestadora] },
    { value: InspectionItemStatus.LaudoNegadoPorPrestadora, label: InspectionItemStatusLabels[InspectionItemStatus.LaudoNegadoPorPrestadora] },
    { value: InspectionItemStatus.AguardandoRevisaoBanco, label: InspectionItemStatusLabels[InspectionItemStatus.AguardandoRevisaoBanco] },
    { value: InspectionItemStatus.LaudoNegadoPorBanco, label: InspectionItemStatusLabels[InspectionItemStatus.LaudoNegadoPorBanco] },
    { value: InspectionItemStatus.CorrecaoPeloInspetorBanco, label: InspectionItemStatusLabels[InspectionItemStatus.CorrecaoPeloInspetorBanco] },
    { value: InspectionItemStatus.LaudoAceitoPeloBanco, label: InspectionItemStatusLabels[InspectionItemStatus.LaudoAceitoPeloBanco] },
    { value: InspectionItemStatus.TelevistoriaAgendada, label: InspectionItemStatusLabels[InspectionItemStatus.TelevistoriaAgendada] },
    { value: InspectionItemStatus.TelevistoriaFinalizada, label: InspectionItemStatusLabels[InspectionItemStatus.TelevistoriaFinalizada] },
    { value: InspectionItemStatus.LaudoDevolvido, label: InspectionItemStatusLabels[InspectionItemStatus.LaudoDevolvido] },
  ];

  // Filtrar dados baseado nos status selecionados
  const filteredData = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return data;
    }
    // Converter números do enum para nomes (keys) do enum para comparação com o status string do backend
    const selectedStatusNames = selectedStatuses.map(statusNum => InspectionItemStatus[statusNum]);
    return data.filter(inspection => selectedStatusNames.includes(inspection.status));
  }, [data, selectedStatuses]);

  // Calcular centro do mapa baseado nos dados filtrados
  const mapCenter: [number, number] = useMemo(() => {
    if (filteredData.length === 0) return [-25.4284, -49.2733]; // Curitiba como padrão

    const avgLat = filteredData.reduce((sum, item) => sum + item.latitude, 0) / filteredData.length;
    const avgLng = filteredData.reduce((sum, item) => sum + item.longitude, 0) / filteredData.length;

    return [avgLat, avgLng];
  }, [filteredData]);

  if (loading) {
    return (
      <Card style={{ height: 600 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 550 }}>
          <Spin size="large" tip="Carregando inspeções..." />
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card style={{ height: 600 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 550 }}>
          <Empty description="Nenhuma inspeção com localização encontrada" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <style>{`
        .custom-marker-icon {
          background: transparent !important;
          border: none !important;
        }
        .marker-cluster-custom {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-popup-content {
          margin: 8px 12px;
        }
      `}</style>
      <Card
        title={
          <Space size="middle">
            <EnvironmentOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Mapa de Inspeções</span>
            <span style={{ fontSize: 14, color: '#595959' }}>
              {selectedStatuses.length > 0
                ? `${filteredData.length} de ${data.length} inspeções`
                : `${data.length} ${data.length === 1 ? 'inspeção' : 'inspeções'}`
              }
            </span>
          </Space>
        }
        extra={
          <Select
            mode="multiple"
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            placeholder="Filtrar por status"
            style={{ width: 300 }}
            allowClear
            maxTagCount="responsive"
            options={statusOptions}
          />
        }
        style={{ height: 600 }}
        bodyStyle={{ padding: 0, height: 'calc(100% - 57px)' }}
      >
        <MapContainer
          center={mapCenter}
          zoom={10}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
          key={`${mapCenter[0]}-${mapCenter[1]}`}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MarkerClusterGroup
            inspections={filteredData}
            onInspectionClick={onInspectionClick}
          />
        </MapContainer>
      </Card>
    </>
  );
};

export default InspectionMapView;
